import { FC, useContext, ReactNode, useEffect, useReducer } from "react";

import { AppContext, AppContextState, INITIAL_STATE } from "./AppContext.ts";
import { AppReducer } from "./AppReducer.ts";
import { useWidth } from "@/hooks/useWidth.ts";
import { isWidthDown } from "@mui/material/Hidden/withWidth";
import { AuthTempInfo, AuthUser } from "@/types/Auth.ts";
import { AdminRole } from "@/types/AppRole.ts";
import { ControlPanelUser } from "@/types/ControlPanel.ts";
interface AppProviderProps {
  children: ReactNode | ReactNode[];
}

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const [{ auth, menu, layout, loaded, controlPanelUser }, dispatch] = useReducer(
    AppReducer,
    INITIAL_STATE
  );

  const width = useWidth();

  useEffect(() => {
    dispatch({ type: "setBreakPoint", payload: width });

    if (isWidthDown("sm", width)) {
      dispatch({ type: "setDevice", payload: "mobile" });
    } else if (isWidthDown("md", width)) {
      dispatch({ type: "setDevice", payload: "tablet" });
    } else {
      dispatch({ type: "setDevice", payload: "pc" });
    }
  }, [width]);

  const setAuthUser = (value: AuthUser) => {
    dispatch({ type: "setAuthUser", payload: value });
  };

  const setControlPanelUser = (value: ControlPanelUser) => {
    dispatch({ type: "setControlPanelUser", payload: value });
  };

  const setUserRoles = (value: AdminRole[]) => {
    dispatch({ type: "setUserRoles", payload: value });
  };

  const setAuthTempInfo = (value: AuthTempInfo) => {
    dispatch({ type: "setAuthTempInfo", payload: value });
  };

  const setLoaded = (value: boolean) => {
    dispatch({ type: "setLoaded", payload: value });
  };

  const setMenuOpen = (value: boolean) => {
    dispatch({ type: "setMenuOpen", payload: value });
  };

  const cleanState = () => {
    dispatch({ type: "cleanState" });
  };

  return (
    <AppContext.Provider
      value={{
        auth,
        layout,
        menu,
        loaded,
        controlPanelUser,
        setLoaded,
        setMenuOpen,
        setUserRoles,
        setAuthUser,
        setControlPanelUser,
        setAuthTempInfo,
        cleanState,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextState => useContext(AppContext);
